//#region packages
import axios from 'axios';
import i18n, { t } from 'i18next';
import Swal from 'sweetalert2';

//#endregion

//#region local imports
import IndexedDbWrapper from './browserStorage/indexedDb-wrapper';
import { ApiBaseURL } from '../../types/constants/apiUrls/api-urls';
import Helper from '../../utils/helper';
import keycloak from '../../keycloak';
i18n.on('languageChanged', (ev) => {
    localStorage.setItem('lan', ev);
});

/** Login bootstrap: check-operator runs before username is stored in localStorage. */
const CHECK_OPERATOR_URL_FRAGMENT = 'check-operator-exist';

let isForcingLogoutDueToMissingUsername = false;

const getToken = function () {
    return localStorage.getItem('access_token');
};

const getRegionId = function () {
    return JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId || 0;
};

const getLang = function () {
    return localStorage.getItem('lan');
};
const getCountryId = function () {
    return JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId || 0;
};
const getStoreCode = function () {
    return localStorage.getItem('storeCode');
};
const isStoreEmployee = function () {
    return localStorage.getItem('isStoreEmployee') === 'true' ? 'true' : 'false';
};
const getUsername = function () {
    let username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username;
    if (username === undefined || !username || username === null) {
        return '';
    } else {
        return username;
    }
};

const getOperatorId = function () {
    let operatorId = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.operatorId;
    if (operatorId === undefined || !operatorId || operatorId === null) {
        return import.meta.env.VITE_OPERATORID!;
    } else {
        return operatorId;
    }
};

const isBlankUsername = (username: unknown): boolean =>
    username == null || String(username).trim() === '';

const isCheckOperatorRequest = (config: { url?: string } | undefined): boolean =>
    String(config?.url || '').includes(CHECK_OPERATOR_URL_FRAGMENT);

/**
 * Username in x-api-context is required for authenticated API calls.
 * When it is missing mid-session, force re-login (skip check-operator bootstrap).
 */
const forceReLoginDueToMissingUsername = (): void => {
    if (isForcingLogoutDueToMissingUsername) {
        return;
    }
    isForcingLogoutDueToMissingUsername = true;

    Swal.fire({
        icon: 'warning',
        title: i18n.t('general.sessionExpiredError'),
        allowOutsideClick: false,
        allowEscapeKey: false,
    }).then(() => {
        localStorage.clear();
        keycloak.logout();
    });
};

const ensureUsernameOrForceLogout = (config: { url?: string } | undefined): boolean => {
    if (!getToken() || isCheckOperatorRequest(config) || !isBlankUsername(getUsername())) {
        return true;
    }
    forceReLoginDueToMissingUsername();
    return false;
};

const buildApiContextHeader = (): string => {
    const selectedCountry = localStorage.getItem('selectedCountry');
    const parsedCountry = selectedCountry ? JSON.parse(selectedCountry) : 'TR';
    const countryCode = parsedCountry?.countryCode || 'TR';
    return (
        '{storeCode:"' +
        getStoreCode() +
        '",isStoreEmployee:' +
        isStoreEmployee() +
        ',operatorId:' +
        getOperatorId() +
        ',environment:"' +
        import.meta.env.VITE_ENVIRONMENT +
        '",regionid:' +
        getRegionId() +
        ',applicationid:1,culture:"' +
        getLang() +
        '",countryid:' +
        getCountryId() +
        ',countryCode:"' +
        countryCode +
        '",username:"' +
        getUsername() +
        '"}'
    );
};

const STATUS_ERROR_TITLE_KEYS: Record<number, string> = {
    401: 'general.sessionExpiredError',
    403: 'general.authorizationError',
    404: 'general.notFoundError',
    500: 'general.serviceError',
};

const parseResponseData = async (data: any): Promise<any> => {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await data.text();
            if (!text) {
                return '';
            }
            try {
                return JSON.parse(text);
            } catch {
                return text;
            }
        } catch {
            return '';
        }
    }
    return data;
};

const extractErrorCodeOrMessage = (data: any): string => {
    if (typeof data === 'string') {
        return data;
    }
    if (data && typeof data === 'object') {
        return data.message || data.detail || data.title || data.error || data.code || '';
    }
    return '';
};

const resolveErrorDisplayMessage = (data: any): string => {
    const errorCodeOrMessage = extractErrorCodeOrMessage(data);
    if (!errorCodeOrMessage) {
        return '';
    }

    const translationKey = `errors.${errorCodeOrMessage}`;
    const rpaTranslationKey = `rpaResender.errors.${errorCodeOrMessage}`;
    if (i18n.exists(translationKey)) {
        return i18n.t(translationKey);
    }
    if (i18n.exists(rpaTranslationKey)) {
        return i18n.t(rpaTranslationKey);
    }
    return errorCodeOrMessage;
};

const buildErrorHtml = (url: any, status: any, displayMessage: string): string =>
    displayMessage ? `${displayMessage}<br/><br/>${url}<br/>${status}` : `${url}<br/>${status}`;

const swalError = (url: any, errorResponse: any) => {
    const status = errorResponse.status;
    const displayMessage = resolveErrorDisplayMessage(errorResponse.data);
    const titleKey = STATUS_ERROR_TITLE_KEYS[status] || 'general.error';

    Swal.fire({
        icon: 'error',
        title: i18n.t(titleKey),
        html: buildErrorHtml(url, status, displayMessage),
    });
};
const fullUrl = (options: any) => {
    let paramString = Helper.serializeParams(options.params);
    if (paramString !== '') {
        paramString = '?' + paramString;
    }

    let url = ApiBaseURL + options.url + paramString;
    // console.info(url);
    return url;
};

const client = axios.create({
    baseURL: ApiBaseURL,
    paramsSerializer: Helper.serializeParams,
    timeout: 8 * 60 * 1000,
    // Uzun süren işlemler için özel ayarlar
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
});

// NOTE cors hatası alınıyor bazen. önerilenlerde bu vardı. api tarafında da sorun olabilir. bi araştırılması gerekiliyor...
// client.defaults.withCredentials = true;
// client.defaults.crossDomain = true;

// Keep-alive bağlantıları için ayarlar
client.defaults.httpAgent = { keepAlive: true };
client.defaults.httpsAgent = { keepAlive: true };

client.interceptors.request.use(
    (config: any) => {
        if (!ensureUsernameOrForceLogout(config)) {
            return Promise.reject(new Error('Missing username in x-api-context'));
        }

        // Do something before request is sent
        config.headers['Authorization'] = 'Bearer ' + getToken();

        // Skip Content-Type for FormData to let axios add boundary automatically
        if (!config.skipContentType) {
            if (config?.requestType === 'FromForm') {
                config.headers['Content-Type'] = 'multipart/form-data';
            } else {
                config.headers['Content-Type'] = 'application/json';
            }
        }
        config.headers['x-api-context'] = buildApiContextHeader();

        return config;
    },
    (error) => Promise.reject(error)
);

const dispatchApiLog = (log: any) => {
    const event = new CustomEvent('api-log', { detail: log });
    window.dispatchEvent(event);
};

const request = function (options: any) {
    const onSuccess = function (response: any) {
        if (options.responseType === 'blob' && !options.skipBlobAutoOpen) {
            window.open(URL.createObjectURL(response.data));
        }
        if (options.useCache) {
            IndexedDbWrapper.addItemToDb('requests', fullUrl(options));
        }

        // Log successful response
        dispatchApiLog({
            id: Date.now().toString(),
            timestamp: Date.now(),
            url: response.config.url,
            method: response.config.method?.toUpperCase(),
            request: {
                params: response.config.params,
                data: response.config.data,
                headers: response.config.headers,
            },
            response: response.data,
            status: response.status,
        });

        return response.data;
    };

    const onCacheSuccess = function (res: any) {
        if (res.length > 0) {
            //cache time tanımlanmadı ise varsayılan 2 saat
            if (res[0].insert_date + (options.cacheTime ? options.cacheTime : 2) * 60 * 60 * 1000 < Date.now()) {
                IndexedDbWrapper.deleteItemFromDb('requests', res[0].id);
            } else {
                return res[0].response;
            }
        }

        return client(options).then(onSuccess).catch(onError);
    };

    const onError = async function (error: any) {
        if (isForcingLogoutDueToMissingUsername) {
            return Promise.reject(error.response || error.message || error);
        }

        if (error.response) {
            error.response.data = await parseResponseData(error.response.data);

            if (error.response.status === 498) {
                Swal.fire({
                    icon: 'warning',
                    title: t('general.customPolicyFailed'),
                    html: `${t(`general.${error.response.data.message}`)}
                    </br>
                    Url: <b>${error.response.config.url}</b>
                    `,
                });
                return;
            }
            const url = error.response.config.url ? error.response.config.url : ' ';
            if (!options.skipErrorModal) {
                swalError(url, error.response);
            }

            // Log error response
            dispatchApiLog({
                id: Date.now().toString(),
                timestamp: Date.now(),
                url: error.response.config.url,
                method: error.response.config.method?.toUpperCase(),
                request: {
                    params: error.response.config.params,
                    data: error.response.config.data,
                    headers: error.response.config.headers,
                },
                error: error.response.data,
                status: error.response.status,
            });
        } else {
            Swal.fire('', error.toString(), 'error');

            // Log network error
            dispatchApiLog({
                id: Date.now().toString(),
                timestamp: Date.now(),
                url: options.url,
                method: options.method?.toUpperCase(),
                request: {
                    params: options.params,
                    data: options.data,
                },
                error: error.message,
            });
        }

        return Promise.reject(error.response || error.message);
    };

    if (options.useCache) {
        return IndexedDbWrapper.getItemFromDb('requests', fullUrl(options)).then(onCacheSuccess);
    } else {
        return client(options).then(onSuccess).catch(onError);
    }
};

/**
 * Uzun süren işlemler (fotoğraf üretme, video işleme vb.) için özel request fonksiyonu
 * Daha uzun timeout ve özel ayarlara sahip
 */
const longRunningRequest = function (options: any) {
    // Uzun süren işlemler için özel client oluştur
    const longRunningClient = axios.create({
        baseURL: ApiBaseURL,
        paramsSerializer: Helper.serializeParams,
        timeout: 15 * 60 * 1000, // 15 dakika
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        // Bağlantı ayarları
        httpAgent: { keepAlive: true },
        httpsAgent: { keepAlive: true },
    });

    // Request interceptor'ı ekle
    longRunningClient.interceptors.request.use(
        (config: any) => {
            if (!ensureUsernameOrForceLogout(config)) {
                return Promise.reject(new Error('Missing username in x-api-context'));
            }

            config.headers['Authorization'] = 'Bearer ' + getToken();

            if (!config.skipContentType) {
                if (config?.requestType === 'FromForm') {
                    config.headers['Content-Type'] = 'multipart/form-data';
                } else {
                    config.headers['Content-Type'] = 'application/json';
                }
            }

            config.headers['x-api-context'] = buildApiContextHeader();

            return config;
        },
        (error) => Promise.reject(error)
    );

    const onSuccess = function (response: any) {
        if (options.responseType === 'blob' && !options.skipBlobAutoOpen) {
            window.open(URL.createObjectURL(response.data));
        }

        dispatchApiLog({
            id: Date.now().toString(),
            timestamp: Date.now(),
            url: response.config.url,
            method: response.config.method?.toUpperCase(),
            request: {
                params: response.config.params,
                data: response.config.data,
                headers: response.config.headers,
            },
            response: response.data,
            status: response.status,
        });

        return response.data;
    };

    const onError = async function (error: any) {
        if (isForcingLogoutDueToMissingUsername) {
            return Promise.reject(error.response || error.message || error);
        }

        if (error.response) {
            error.response.data = await parseResponseData(error.response.data);

            if (error.response.status === 498) {
                Swal.fire({
                    icon: 'warning',
                    title: t('general.customPolicyFailed'),
                    html: `${t(`general.${error.response.data.message}`)}
                    </br>
                    Url: <b>${error.response.config.url}</b>
                    `,
                });
                return;
            }
            const url = error.response.config.url ? error.response.config.url : ' ';
            if (!options.skipErrorModal) {
                swalError(url, error.response);
            }

            dispatchApiLog({
                id: Date.now().toString(),
                timestamp: Date.now(),
                url: error.response.config.url,
                method: error.response.config.method?.toUpperCase(),
                request: {
                    params: error.response.config.params,
                    data: error.response.config.data,
                    headers: error.response.config.headers,
                },
                error: error.response.data,
                status: error.response.status,
            });
        } else {
            // Network error - daha detaylı mesaj
            const errorMessage =
                error.code === 'ECONNABORTED' ? 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.' : error.message;

            Swal.fire({
                icon: 'error',
                title: 'Network Error',
                text: errorMessage,
                footer: 'İşlem çok uzun sürüyorsa, lütfen sistem yöneticinize başvurun.',
            });

            dispatchApiLog({
                id: Date.now().toString(),
                timestamp: Date.now(),
                url: options.url,
                method: options.method?.toUpperCase(),
                request: {
                    params: options.params,
                    data: options.data,
                },
                error: error.message,
            });
        }

        return Promise.reject(error.response || error.message);
    };

    return longRunningClient(options).then(onSuccess).catch(onError);
};

export default request;
export { longRunningRequest, getUsername };
